/**
 * CPS calculation system
 * Headless - no GUI
 */

import type { GameState } from '../state.js';
import { PRODUCERS, getMilestoneMultiplier } from '../data/producers.js';
import { CLICK_UPGRADES, SPECIAL_UPGRADES } from '../data/upgrades.js';
import { getConquestCpsMult, getConquestTapMult } from './conquest.js';
import { getCurrentRank } from '../data/ranks.js';
import { getEquippedAgentBonus } from './agents.js';
import { getAllFactionBonus } from './faction.js';

/** Calculate CPS from a single producer tier */
export function getProducerCps(state: GameState, producerId: string): number {
  const def = PRODUCERS.find(p => p.id === producerId);
  if (!def) return 0;
  const owned = state.producers[producerId]?.count ?? 0;
  if (owned === 0) return 0;

  const milestone = getMilestoneMultiplier(owned);

  // Achievement multiplier for specific producers
  let achMult = 1;
  // G01~G12 give specific producer multipliers
  const achProducerBoosts: Record<string, { id: string; mult: number }[]> = {
    informant: [
      { id: 'G01', mult: 1.5 },
      { id: 'G02', mult: 2 },
    ],
    hacker: [
      { id: 'G03', mult: 2 },
      { id: 'G04', mult: 3 },
    ],
    disguiser: [{ id: 'G05', mult: 2 }],
    mindController: [{ id: 'G06', mult: 2 }],
    secretLab: [{ id: 'G07', mult: 2 }],
    mediaCell: [{ id: 'G08', mult: 2 }],
    shadowGovt: [{ id: 'G09', mult: 2 }],
    deepBase: [{ id: 'G10', mult: 2 }],
    orbitalStation: [{ id: 'G11', mult: 2 }],
  };
  const boosts = achProducerBoosts[producerId];
  if (boosts) {
    for (const b of boosts) {
      if (state.achievements[b.id]?.unlocked) achMult *= b.mult;
    }
  }

  // Special upgrade multiplier for this producer
  let specialMult = 1;
  for (const su of SPECIAL_UPGRADES) {
    if (!state.specialUpgradesBought.includes(su.id)) continue;
    if (su.effect.type === 'producerMultiplier' && su.effect.producerIds.includes(producerId)) {
      specialMult *= su.effect.multiplier;
    }
  }

  return def.baseCps * owned * milestone * achMult * specialMult;
}

/** Calculate total CPS from all sources */
export function getTotalCps(state: GameState): number {
  let baseCps = 0;
  for (const p of PRODUCERS) {
    baseCps += getProducerCps(state, p.id);
  }

  // Global multipliers
  let globalMult = 1;

  // Prestige multiplier (galaxyMultiplier: x1.5 per level, multiplicative)
  const galaxyLv = state.prestigeUpgrades.galaxyMultiplier;
  if (galaxyLv > 0) globalMult *= Math.pow(1.5, galaxyLv);

  // Prestige2 dimension accel (x5 per level)
  const dimAccelLv = state.prestige2Upgrades.dimensionAccel;
  if (dimAccelLv > 0) globalMult *= Math.pow(5, dimAccelLv);

  // Special upgrades: allProducerMultiplier, allCpsMultiplier
  for (const su of SPECIAL_UPGRADES) {
    if (!state.specialUpgradesBought.includes(su.id)) continue;
    if (su.effect.type === 'allProducerMultiplier') {
      globalMult *= su.effect.multiplier;
    } else if (su.effect.type === 'allCpsMultiplier') {
      globalMult *= su.effect.multiplier;
    }
  }

  // Achievement CPS bonuses (percentage)
  let achCpsPct = 0;
  const achCpsBonuses: Record<string, number> = {
    R01: 5, R02: 5, R03: 5, R04: 10, R05: 10,
    R06: 15, R07: 15, R08: 20, R09: 20, R10: 30,
    H02: 5, H03: 10, H05: 50, H06: 100,
    S03: 10, G12: 100, S08: 200,
  };
  for (const [achId, pct] of Object.entries(achCpsBonuses)) {
    if (state.achievements[achId]?.unlocked) achCpsPct += pct;
  }
  globalMult *= (1 + achCpsPct / 100);

  // Active boosts
  const now = Date.now();
  for (const boost of state.activeBoosts) {
    if (now < boost.endsAt) {
      if (boost.type === 'cps2x' || boost.type === 'cps3x' || boost.type === 'cps5x') {
        globalMult *= boost.multiplier;
      }
    }
  }

  // Conquest facility CPS bonus
  globalMult *= getConquestCpsMult(state);

  // Agent collection CPS bonus
  const agentCpsMult = getEquippedAgentBonus(state, 'cps_pct');
  globalMult *= agentCpsMult;

  // Faction CPS bonus (all factions independently)
  const factionCpsMult = getAllFactionBonus(state, 'cps_pct');
  globalMult *= factionCpsMult;

  return baseCps * globalMult;
}

/** Calculate tap power (IP per tap) */
export function getTapPower(state: GameState): number {
  let flat = state.baseTapPower;

  // Click upgrades (flat bonus)
  for (const idx of state.clickUpgradesBought) {
    const upg = CLICK_UPGRADES[idx];
    if (upg?.flatBonus) flat += upg.flatBonus;
  }

  // Prestige tap enhance (x2 per level)
  const tapLv = state.prestigeUpgrades.tapEnhance;
  if (tapLv > 0) flat *= Math.pow(2, tapLv);

  // Special upgrade: clickMultiplier
  for (const su of SPECIAL_UPGRADES) {
    if (!state.specialUpgradesBought.includes(su.id)) continue;
    if (su.effect.type === 'clickMultiplier') {
      flat *= su.effect.multiplier;
    }
  }

  // Achievement tap bonuses
  let tapPct = 0;
  const achTapBonuses: Record<string, number> = {
    T01: 0, // +1 flat (already baseTapPower)
    T02: 5, T03: 10, T04: 15, T05: 20, T06: 25,
  };
  for (const [achId, pct] of Object.entries(achTapBonuses)) {
    if (state.achievements[achId]?.unlocked) tapPct += pct;
  }
  flat *= (1 + tapPct / 100);

  // CPS-based click upgrades
  const cps = getTotalCps(state);
  let cpsBonus = 0;
  for (const idx of state.clickUpgradesBought) {
    const upg = CLICK_UPGRADES[idx];
    if (upg?.cpsPercent) cpsBonus += cps * upg.cpsPercent / 100;
  }

  // Active boost: tap multiplier
  const now = Date.now();
  let tapMult = 1;
  for (const boost of state.activeBoosts) {
    if (now < boost.endsAt && (boost.type === 'tap3x' || boost.type === 'tap5x' || boost.type === 'tap10x')) {
      tapMult *= boost.multiplier;
    }
  }

  // Combo multiplier: 1 + comboCount * 0.01 (capped at 2.0 or 6.0 with special)
  const comboCap = state.specialUpgradesBought.includes('comboMaster') ? 6.0 : 2.0;
  const achComboCapBonus = state.achievements['T08']?.unlocked ? 0.5 : 0;
  const effectiveCap = comboCap + achComboCapBonus;
  const comboMult = Math.min(effectiveCap, 1 + state.comboCount * 0.01);

  // Conquest facility tap bonus
  tapMult *= getConquestTapMult(state);

  // Rank promotion tap multiplier
  const rankDef = getCurrentRank(state.rank ?? 'E');
  tapMult *= rankDef.tapMultiplier;

  // Agent collection tap bonus
  const agentTapMult = getEquippedAgentBonus(state, 'tap_pct');
  tapMult *= agentTapMult;

  // Faction tap bonus (all factions independently)
  const factionTapMult = getAllFactionBonus(state, 'tap_pct');
  tapMult *= factionTapMult;

  return Math.floor((flat + cpsBonus) * tapMult * comboMult);
}

/** Get critical hit chance (0.0 ~ 1.0) */
export function getCritChance(state: GameState): number {
  // Base 10% + 3% per prestige level
  const base = 0.10;
  const prestigeBonus = state.prestigeUpgrades.criticalChance * 0.03;

  // Click upgrade crit chance bonuses
  let clickBonus = 0;
  for (const idx of state.clickUpgradesBought) {
    const upg = CLICK_UPGRADES[idx];
    if (upg?.critChance) clickBonus += upg.critChance;
  }

  return Math.min(base + prestigeBonus + clickBonus, 0.5); // cap at 50%
}

/** Get critical hit multiplier */
export function getCritMultiplier(state: GameState): number {
  // Base x3 + x1 per prestige level
  const base = 3;
  const prestigeBonus = state.prestigeUpgrades.criticalDamage * 1;

  // Click upgrade crit multiplier bonuses
  let clickBonus = 0;
  for (const idx of state.clickUpgradesBought) {
    const upg = CLICK_UPGRADES[idx];
    if (upg?.critMult) clickBonus += upg.critMult;
  }

  return base + prestigeBonus + clickBonus;
}

/** Get combo timeout in ms */
export function getComboTimeout(state: GameState): number {
  let base = 2000; // 2 seconds
  // Prestige comboKeep: +0.5s per level
  base += state.prestigeUpgrades.comboKeep * 500;
  // Achievement C01: +0.3s
  if (state.achievements['C01']?.unlocked) base += 300;
  // Achievement T07: +0.5s
  if (state.achievements['T07']?.unlocked) base += 500;
  return base;
}
